import React, { useContext, useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Box, Grid, } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import { genericGet } from "../../services/apiServices";
import { GET_PAYMENT_REGISTER } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomFilter from "../common/CustomFilter";
import CustomTextField from "../common/CustomTextField";
import CustomAutocomplete from "../common/CustomAutoComplete";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import { genericPost } from "../../services/authServices";
import CustomDataTable from "../common/CustomDataTable";
import CustomButton from "../common/CustomButton";
import { getSuppliersList } from "../../services/CommonServices";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";


const title = "Payment Register";
const PaymentRegister = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();

    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    // ✅ Pagination states
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)
        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        clientCompanyId: '',
        fromDate: '',
        toDate: '',


    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                clientCompanyId: '',
                fromDate: '',
                toDate: '',

            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
    }
    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'voucherNo', header: 'Voucher No.', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment ID', size: 100, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'supplierName', header: 'Supplier Name', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            { accessorKey: 'referenceAmount', header: 'Amount', size: 100, tableStyle: { textAlign: "center" }, },
        ],
        [permission]
    );
    const fetchData = async (pageIndex = 0, pageSize = 10,) => {
        setLoading(true);
        try {
            // const response = await genericGet({ url: GET_PAYMENT_REGISTER });
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_PAYMENT_REGISTER, payload, {});

            if (response.data.success && response.data.paymentVoucher) {
                const paymentVoucherData = response.data.paymentVoucher.map((voucher, index) => {
                    const totalReferenceAmount = (voucher.particulars || []).reduce((sum, p) => {
                        return sum + parseFloat(p.referenceAmount || 0);
                    }, 0);

                    const currencySymbol = voucher?.currency?.currency_symbol || '';
                    const formattedAmount = `${currencySymbol} ${totalReferenceAmount.toFixed(2)}`;

                    return {
                        paymentVoucherId: voucher.paymentVoucherId || '',
                        index: (index + 1).toString().padStart(2, '0'),
                        date: companyDateFormat(voucher?.date) || '',
                        voucherNo: voucher.paymentVoucherNo || '',
                        shipmentId: voucher?.importShipment?.shipmentId || '',
                        supplierName: voucher?.clientCompany?.clientCompanyName || '',
                        referenceAmount: formattedAmount,
                        particulars: voucher.particulars || [] // ✅ Add this to enable row expansion
                    };
                });

                setData(paymentVoucherData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            } else {
                setError('No payment vouchers found');
            }
        } catch (error) {
            setError('Error fetching payment vouchers');
            console.error('Error fetching payment vouchers', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token])

    const enableExpanding = true;
    const getRowCanExpand = () => true;

    const renderDetailPanel = ({ row }) => {
        const particulars = row.original?.particulars || [];

        return (
            <Box sx={{ background: "#f9f9f9", p: 0 }}>
                <Box sx={{ display: 'flex', fontWeight: '600', mb: 1, borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
                    <Box sx={{ width: '100px', textAlign: 'center' }}>Date</Box>
                    <Box sx={{ width: '120px', textAlign: 'center' }}>Voucher No</Box>
                    <Box sx={{ width: '180px', textAlign: 'center' }}>PI No</Box>
                    <Box sx={{ width: '180px', textAlign: 'center' }}>CI No</Box>
                    <Box sx={{ width: '150px', textAlign: 'center' }}>Payment Name</Box>
                    <Box sx={{ width: '150px', textAlign: 'center' }}>Amount</Box>
                </Box>

                {particulars.length > 0 ? (
                    particulars.map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', fontSize: '14px', borderBottom: '1px dashed #ddd', py: 0.5 }}>
                            <Box sx={{ width: '100px', textAlign: 'center' }}>{companyDateFormat(item.date || '-')}</Box>
                            <Box sx={{ width: '120px', textAlign: 'center' }}>{row.original.voucherNo || '-'}</Box>
                            <Box sx={{ width: '180px', textAlign: 'center' }}>{item.importPiNoList?.proformaInvoiceNo || '-'}</Box>
                            <Box sx={{ width: '180px', textAlign: 'center' }}>{item.importCiNoList?.commercialInvoiceNo || '-'}</Box>
                            <Box sx={{ width: '150px', textAlign: 'center' }}>{item.paymentType?.paymentName || '-'}</Box>
                            <Box sx={{ width: '150px', textAlign: 'center' }}>
                                {`${item.currency?.currency_symbol || ''} ${parseFloat(item.referenceAmount || 0).toFixed(2)}`}
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>No sub-row data available</Box>
                )}
            </Box>
        );
    };

    const defaultView = 'paymentregister';
    const mainContent = (
        <>
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                enableExpanding={enableExpanding}
                getRowCanExpand={getRowCanExpand}
                renderDetailPanel={renderDetailPanel}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("clientCompanyId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.clientCompanyId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>


                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}
        </>

    );
};
export default PaymentRegister;
